import React, { useState } from 'react';
import { Phone, Mail, ArrowRight, CheckCircle2, X, CreditCard, AlertCircle, Building2 } from 'lucide-react';
import { submitCardApplication } from '../lib/formService';
import { useLocation } from 'react-router-dom';

interface CardApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  cardName?: string;
  bankName?: string;
}

const CardApplicationModal: React.FC<CardApplicationModalProps> = ({
  isOpen,
  onClose,
  cardName,
  bankName
}) => {
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [kvkkConsent, setKvkkConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    phone?: string;
    email?: string;
    consent?: string;
    submit?: string;
  }>({});

  if (!isOpen) return null;

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setPhone(value);
      if (errors.phone) {
        setErrors(prev => ({ ...prev, phone: undefined }));
      }
    }
  };

  const formatPhone = (value: string) => {
    if (!value) return '';
    const match = value.match(/^(\d{0,3})(\d{0,3})(\d{0,2})(\d{0,2})$/);
    if (!match) return value;
    return match
      .slice(1)
      .filter(Boolean)
      .join(' ');
  };

  const validateStep = () => {
    const newErrors: typeof errors = {};

    if (!phone || phone.length < 10) {
      newErrors.phone = 'Lütfen geçerli bir telefon numarası girin (10 haneli)';
    }

    if (!email || !email.includes('@')) {
      newErrors.email = 'Lütfen geçerli bir e-posta adresi girin';
    }

    if (!kvkkConsent) {
      newErrors.consent = 'Devam etmek için KVKK metnini onaylamanız gerekmektedir';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setIsSubmitting(true);
    setErrors(prev => ({ ...prev, submit: undefined }));

    try {
      const result = await submitCardApplication({
        card_name: cardName,
        bank_name: bankName,
        phone_number: phone,
        email: email,
        kvkk_consent: kvkkConsent,
        marketing_consent: marketingConsent,
        source_page: location.pathname,
        source_section: 'card_application_modal'
      });

      if (result.success) {
        setStep(2);
      } else {
        setErrors({ submit: result.error || 'Bir hata oluştu. Lütfen tekrar deneyin.' });
      }
    } catch (error) {
      setErrors({ submit: 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-slate-800 mb-3">
                Kredi Kartı Başvurusu
              </h2>
              {cardName && (
                <p className="text-slate-600">
                  {cardName} için başvuru yapıyorsunuz
                </p>
              )}
            </div>

            {/* Card Info */}
            {(cardName || bankName) && (
              <div className="bg-gradient-to-br from-[#F7BA45]/10 to-slate-50 rounded-xl p-6 border border-[#F7BA45]/20">
                <div className="flex items-center gap-4">
                  <div className="bg-[#F7BA45]/10 p-3 rounded-lg">
                    <CreditCard size={24} className="text-[#F7BA45]" />
                  </div>
                  <div>
                    {cardName && (
                      <div className="text-lg font-semibold text-slate-800">{cardName}</div>
                    )}
                    {bankName && (
                      <div className="text-sm text-slate-600">{bankName}</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {/* Phone Input */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  Telefon Numarası *
                </label>
                <div className="flex gap-2">
                  <div className="w-20">
                    <div className="flex items-center h-12 px-3 border border-slate-200 rounded-lg bg-slate-50 text-slate-600 font-medium">
                      +90
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                      <input
                        type="text"
                        value={formatPhone(phone)}
                        onChange={handlePhoneChange}
                        placeholder="(___) ___ __ __"
                        className={`w-full h-12 pl-10 pr-4 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                          errors.phone ? 'border-red-300 bg-red-50' : 'border-slate-200'
                        }`}
                      />
                    </div>
                  </div>
                </div>
                {errors.phone && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.phone}
                  </p>
                )}
              </div>

              {/* Email Input */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  E-posta Adresi *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errors.email) {
                        setErrors(prev => ({ ...prev, email: undefined }));
                      }
                    }}
                    placeholder="ornek@email.com"
                    className={`w-full h-12 pl-10 pr-4 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                      errors.email ? 'border-red-300 bg-red-50' : 'border-slate-200'
                    }`}
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Consent Checkboxes */}
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex items-start">
                    <div className="bg-white border-2 rounded-lg w-6 h-6 flex flex-shrink-0 justify-center items-center
                      focus-within:border-[#F7BA45] transition-colors">
                      <input
                        type="checkbox"
                        className="opacity-0 absolute"
                        checked={kvkkConsent}
                        onChange={e => {
                          setKvkkConsent(e.target.checked);
                          if (errors.consent) {
                            setErrors(prev => ({ ...prev, consent: undefined }));
                          }
                        }}
                      />
                      <CheckCircle2 
                        className={`${kvkkConsent ? 'block' : 'hidden'} h-4 w-4 text-[#F7BA45] pointer-events-none`}
                      />
                    </div>
                  </div>
                  <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
                    <a href="#" className="text-[#F7BA45] hover:text-[#e5ac3d] font-medium">KVKK Aydınlatma Metni</a>'ni okudum ve onaylıyorum. *
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex items-start">
                    <div className="bg-white border-2 rounded-lg w-6 h-6 flex flex-shrink-0 justify-center items-center
                      focus-within:border-[#F7BA45] transition-colors">
                      <input
                        type="checkbox"
                        className="opacity-0 absolute"
                        checked={marketingConsent}
                        onChange={e => setMarketingConsent(e.target.checked)}
                      />
                      <CheckCircle2 
                        className={`${marketingConsent ? 'block' : 'hidden'} h-4 w-4 text-[#F7BA45] pointer-events-none`}
                      />
                    </div>
                  </div>
                  <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
                    Kampanya ve fırsatlardan haberdar olmak istiyorum.
                  </span>
                </label>
              </div>
              
              {errors.consent && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle size={14} />
                  {errors.consent}
                </p>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="text-center space-y-8">
            <div className="w-20 h-20 bg-[#F7BA45]/10 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 size={40} className="text-[#F7BA45]" />
            </div>
            
            <div>
              <h2 className="text-2xl font-bold text-slate-800 mb-3">
                Başvurunuz Alındı!
              </h2>
              <p className="text-slate-600 max-w-sm mx-auto">
                Kredi kartı başvurunuz başarıyla alınmıştır. En kısa sürede size dönüş yapılacaktır.
              </p>
            </div>

            <button
              onClick={onClose}
              className="inline-flex items-center justify-center px-8 py-4 bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 font-medium rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Tamam
            </button>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div 
          className="relative bg-white rounded-2xl w-full max-w-lg shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="absolute right-4 top-4 p-2 hover:bg-slate-100 rounded-full transition-colors z-10"
          >
            <X size={20} className="text-slate-500" />
          </button>

          {/* Content */}
          <div className="p-8">
            {renderStep()}

            {/* Submit Button */}
            {step === 1 && (
              <div className="mt-8">
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="w-full bg-[#F7BA45] hover:bg-[#e5ac3d] disabled:bg-slate-300 text-slate-900 font-medium h-14 rounded-xl 
                    transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      BAŞVURU YAP
                      <ArrowRight size={20} />
                    </>
                  )}
                </button>

                {/* Error Messages */}
                {errors.submit && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2 text-red-600 text-sm">
                      <AlertCircle size={16} />
                      <span>{errors.submit}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardApplicationModal;